<script setup lang="ts">
import { Copy, Gift, Send, Users, X } from 'lucide-vue-next';
import { computed } from 'vue';
import { locale, t } from '../i18n';
import { initials, money } from '../state';
import type {
    ReferralFriend,
    ReferralOverview,
    ReferralReward,
} from '../types';

/**
 * «Запросити друга» (Черняк, 2026-09-28): one network's link, a native share, and what
 * every invitation into that network came to. Opened from the network screen (2026-10-06).
 *
 * Invited, bought and credited are three numbers on purpose. A friend counts the moment
 * they register through the link, but brings something only with their first payment — and
 * a reward waits out the club's cooldown before it reaches the balance. «5 друзів, 0 ₴» shown
 * as one figure reads as a programme that cheats; shown apart, each number explains itself.
 */
const props = defineProps<{
    open: boolean;
    overview: ReferralOverview | null;
    loading: boolean;
}>();

defineEmits<{ close: []; share: []; copy: [] }>();

const program = computed(() => props.overview?.program ?? null);

/** How the rewards work, in one paragraph — or why there are none yet. */
const rule = computed((): string => {
    const current = program.value;

    if (!current) {
        return t('Друзі зарахуються вам одразу після реєстрації.');
    }

    if (!current.enabled) {
        return t(
            'У мережі {network} бонуси за друзів зараз не нараховуються. Друзі все одно зарахуються вам.',
            { network: current.network },
        );
    }

    const first = t(
        'Коли друг уперше поповнить рахунок або купить щось від {min} ₴, ви отримаєте {rate}% бонусами в клубі, де він заплатив.',
        {
            min: money(current.min_payment, locale.value),
            rate: money(current.rates[0] ?? 0, locale.value),
        },
    );

    return current.levels > 1 && (current.rates[1] ?? 0) > 0
        ? `${first} ${t('І ще {rate}% від друзів, яких приведе він.', {
              rate: money(current.rates[1], locale.value),
          })}`
        : first;
});

function date(value: string | null | undefined): string {
    if (!value) {
        return '';
    }

    const locales: Record<string, string> = {
        uk: 'uk-UA',
        ru: 'ru-RU',
        en: 'en-GB',
    };

    return new Intl.DateTimeFormat(locales[locale.value] ?? 'uk-UA', {
        day: 'numeric',
        month: 'short',
    }).format(new Date(value));
}

/** The reward that still stands, or the latest one when none does. */
function mainReward(friend: ReferralFriend): ReferralReward | null {
    return (
        friend.rewards.find((reward) =>
            ['pending', 'converted'].includes(reward.status),
        ) ??
        friend.rewards[0] ??
        null
    );
}

/** «Поповнення 500 ₴ · CyberZone Downtown» — what the friend did, and where. */
function rewardReason(reward: ReferralReward): string {
    const what =
        reward.trigger === 'purchase'
            ? t('Покупка {amount} ₴', {
                  amount: money(reward.trigger_amount, locale.value),
              })
            : t('Поповнення {amount} ₴', {
                  amount: money(reward.trigger_amount, locale.value),
              });

    return reward.club ? `${what} · ${reward.club}` : what;
}

function rewardState(reward: ReferralReward): string {
    if (reward.status === 'converted') {
        return t('нараховано');
    }

    if (reward.status === 'pending') {
        return reward.credit_at
            ? t('очікує до {date}', { date: date(reward.credit_at) })
            : t('очікує');
    }

    if (reward.status === 'blocked_by_limit') {
        return t('понад місячний ліміт');
    }

    return t('скасовано');
}

function friendInitials(name: string | null): string {
    const [first, last] = (name ?? '').replace('@', '').split(' ');

    return initials(first, last);
}
</script>

<template>
    <Transition name="sheet" appear>
        <div v-if="open" class="sheet-layer">
            <button
                class="sheet-scrim"
                type="button"
                :aria-label="t('Закрити')"
                @click="$emit('close')"
            />
            <section
                class="basic-sheet invite-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="invite-title"
            >
                <button
                    class="sheet-close"
                    type="button"
                    :aria-label="t('Закрити')"
                    @click="$emit('close')"
                >
                    <X :size="19" />
                </button>

                <div class="invite-badge" aria-hidden="true">
                    <Gift :size="26" :stroke-width="1.8" />
                </div>
                <span v-if="program" class="eyebrow invite-network">{{
                    program.network
                }}</span>
                <h2 id="invite-title">{{ t('Запросити друга') }}</h2>

                <div v-if="loading && !overview" class="invite-skeleton">
                    <span /><span /><span />
                </div>

                <template v-else-if="overview">
                    <p class="invite-rule">{{ rule }}</p>

                    <div class="invite-link">
                        <span class="invite-link__text">{{
                            overview.link ?? overview.code
                        }}</span>
                        <button
                            class="invite-link__copy"
                            type="button"
                            :aria-label="t('Скопіювати посилання')"
                            @click="$emit('copy')"
                        >
                            <Copy :size="18" />
                        </button>
                    </div>
                    <button
                        class="primary-button"
                        type="button"
                        @click="$emit('share')"
                    >
                        <Send :size="18" />
                        {{ t('Поділитися') }}
                    </button>

                    <div class="invite-stats">
                        <div class="invite-stat">
                            <span class="invite-stat__value">{{
                                overview.summary.invited
                            }}</span>
                            <span class="invite-stat__label">{{
                                t('Запрошено')
                            }}</span>
                        </div>
                        <div class="invite-stat">
                            <span class="invite-stat__value">{{
                                overview.summary.bought
                            }}</span>
                            <span class="invite-stat__label">{{
                                t('З них купили')
                            }}</span>
                        </div>
                        <div class="invite-stat">
                            <span class="invite-stat__value"
                                >{{
                                    money(overview.summary.earned, locale)
                                }}
                                ₴</span
                            >
                            <span class="invite-stat__label">{{
                                t('Нараховано')
                            }}</span>
                        </div>
                    </div>
                    <p
                        v-if="overview.summary.pending > 0"
                        class="invite-pending"
                    >
                        {{
                            t('Ще {amount} ₴ чекають нарахування.', {
                                amount: money(overview.summary.pending, locale),
                            })
                        }}
                    </p>

                    <h3 class="invite-heading">{{ t('Ваші друзі') }}</h3>
                    <p v-if="!overview.friends.length" class="invite-empty">
                        <Users :size="20" aria-hidden="true" />
                        {{
                            t(
                                'Поки нікого. Надішліть посилання, і друзі з’являться тут, щойно зареєструються.',
                            )
                        }}
                    </p>
                    <ul v-else class="invite-list">
                        <li
                            v-for="friend in overview.friends"
                            :key="friend.id"
                            class="invite-row"
                        >
                            <span class="invite-row__avatar">{{
                                friendInitials(friend.name)
                            }}</span>
                            <span class="invite-row__body">
                                <span class="invite-row__name">{{
                                    friend.name ?? '—'
                                }}</span>
                                <span class="invite-row__meta">
                                    {{
                                        mainReward(friend)
                                            ? rewardReason(mainReward(friend)!)
                                            : t('З нами з {date}', {
                                                  date: date(
                                                      friend.registered_at,
                                                  ),
                                              })
                                    }}
                                </span>
                            </span>
                            <span
                                v-if="mainReward(friend)"
                                class="invite-row__reward"
                                :class="`is-${mainReward(friend)!.status}`"
                            >
                                <strong
                                    >+{{
                                        money(
                                            mainReward(friend)!.amount,
                                            locale,
                                        )
                                    }}
                                    ₴</strong
                                >
                                <small>{{
                                    rewardState(mainReward(friend)!)
                                }}</small>
                            </span>
                            <span v-else class="invite-row__waiting">{{
                                t('Ще не купував')
                            }}</span>
                        </li>
                    </ul>

                    <template v-if="overview.indirect.length">
                        <h3 class="invite-heading">
                            {{ t('Друзі ваших друзів') }}
                        </h3>
                        <ul class="invite-list">
                            <li
                                v-for="(friend, index) in overview.indirect"
                                :key="`${friend.id}-${index}`"
                                class="invite-row"
                            >
                                <span class="invite-row__avatar">{{
                                    friendInitials(friend.name)
                                }}</span>
                                <span class="invite-row__body">
                                    <span class="invite-row__name">{{
                                        friend.name ?? '—'
                                    }}</span>
                                    <span class="invite-row__meta">
                                        {{
                                            t('Рівень {level}', {
                                                level:
                                                    friend.rewards[0]?.level ??
                                                    2,
                                            })
                                        }}
                                        ·
                                        {{
                                            friend.rewards[0]
                                                ? rewardReason(
                                                      friend.rewards[0],
                                                  )
                                                : ''
                                        }}
                                    </span>
                                </span>
                                <span
                                    v-if="friend.rewards[0]"
                                    class="invite-row__reward"
                                    :class="`is-${friend.rewards[0].status}`"
                                >
                                    <strong
                                        >+{{
                                            money(
                                                friend.rewards[0].amount,
                                                locale,
                                            )
                                        }}
                                        ₴</strong
                                    >
                                    <small>{{
                                        rewardState(friend.rewards[0])
                                    }}</small>
                                </span>
                            </li>
                        </ul>
                    </template>
                </template>
            </section>
        </div>
    </Transition>
</template>

<style scoped>
.invite-sheet {
    max-height: 92dvh;
    padding-top: 30px;
    overflow-y: auto;
    overscroll-behavior: contain;
    text-align: left;
}
.invite-sheet > h2 {
    margin: 4px 46px 8px 0;
}
.invite-network {
    display: block;
    margin-top: 14px;
}
.invite-badge {
    display: grid;
    width: 52px;
    height: 52px;
    place-items: center;
    border-radius: 16px;
    background: color-mix(in srgb, var(--accent) 14%, var(--surface));
    color: var(--accent);
}
.invite-rule {
    margin: 0 0 16px;
    color: var(--hint);
    font-size: 13px;
    line-height: 1.5;
}
.invite-link {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding: 6px 6px 6px 14px;
    border: 1px solid color-mix(in srgb, var(--hint) 18%, transparent);
    border-radius: 14px;
    background: var(--secondary);
}
.invite-link__text {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    color: var(--text);
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.invite-link__copy {
    display: grid;
    width: 38px;
    height: 38px;
    flex: 0 0 38px;
    place-items: center;
    border: 0;
    border-radius: 11px;
    background: var(--surface);
    color: var(--accent);
    cursor: pointer;
}
.invite-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-top: 18px;
}
.invite-stat {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
    padding: 12px 10px;
    border-radius: 14px;
    background: var(--secondary);
}
.invite-stat__value {
    overflow: hidden;
    color: var(--text);
    font-size: 18px;
    font-weight: 650;
    font-variant-numeric: tabular-nums;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.invite-stat__label {
    color: var(--hint);
    font-size: 11px;
    line-height: 1.3;
}
.invite-pending {
    margin: 8px 0 0;
    color: var(--hint);
    font-size: 12px;
}
.invite-heading {
    margin: 22px 0 6px;
    color: var(--text);
    font-size: 14px;
}
.invite-empty {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 6px 0 0;
    color: var(--hint);
    font-size: 13px;
    line-height: 1.5;
}
.invite-empty > svg {
    flex: 0 0 auto;
    color: var(--accent);
}
.invite-list {
    margin: 0;
    padding: 0;
    list-style: none;
}
.invite-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 0;
    border-bottom: 1px solid color-mix(in srgb, var(--hint) 12%, transparent);
}
.invite-row:last-child {
    border-bottom: 0;
}
.invite-row__avatar {
    display: grid;
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    place-items: center;
    border-radius: 50%;
    background: color-mix(in srgb, var(--accent) 14%, var(--surface));
    color: var(--accent);
    font-size: 14px;
    font-weight: 650;
}
.invite-row__body {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 3px;
}
.invite-row__name {
    overflow: hidden;
    color: var(--text);
    font-size: 14px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.invite-row__meta {
    overflow: hidden;
    color: var(--hint);
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.invite-row__reward {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
}
.invite-row__reward strong {
    color: #22a861;
    font-size: 14px;
    font-variant-numeric: tabular-nums;
}
.invite-row__reward small {
    color: var(--hint);
    font-size: 11px;
}
.invite-row__reward.is-pending strong {
    color: var(--accent);
}
.invite-row__reward.is-cancelled strong,
.invite-row__reward.is-reversed strong,
.invite-row__reward.is-blocked_by_limit strong {
    color: var(--hint);
    text-decoration: line-through;
}
.invite-row__waiting {
    flex: 0 0 auto;
    color: var(--hint);
    font-size: 12px;
}
.invite-skeleton {
    display: grid;
    gap: 10px;
    margin-top: 8px;
}
.invite-skeleton span {
    height: 48px;
    border-radius: 14px;
    background: var(--secondary);
    animation: invite-pulse 1.2s ease-in-out infinite;
}
@keyframes invite-pulse {
    50% {
        opacity: 0.5;
    }
}
</style>
